import * as Sentry from '@sentry/browser';
import { isIOS } from 'mobile-device-detect';
import Core from '../Core/Core';
import { IRewardItem } from '../State/reducers/score';
import { InviteBotImage } from '../UI/PopUps/Social/InviteBotImage';
import { getRandom } from '../Utils/Utils';
import { PayloadType } from './PayloadType';
import { IPayloadData, IPayloadGiftData, ISGTrackData, ISGUser, loadStartTime } from './SDK';
import { SDKfbpayments } from './SDKfbpayments';
import { defaultFriend } from './SDKlocal';
import { IMediaParams } from './SDKmessages';
import HelperTracking, { HelperTrackStages } from '../Managers/Tracking/HelperTracking';
import { IFBTrackData } from '../Managers/Tracking/Tracking';
import { XPromoTracking } from '../Managers/Tracking/XPromoTracking';
import ContextFilter = FBInstant.ContextFilter;
import Intent = FBInstant.Intent;
import ContextPlayer = FBInstant.ContextPlayer;

export interface IFBSharePayload {
    intent: Intent;
    image: string;
    text: string;
    data: any;
    surface?: string;
    media?: IMediaParams;
}

export type FBId = string;
export type Url = string;

export interface IFBFriend {
    userId: FBId;
    name: string;
    avatar: Url;
}

export class SDKfb {
    public loadingPromise: Promise<any>;
    public loadResolve: any;
    public readonly payments: SDKfbpayments;

    constructor() {
        this.payments = new SDKfbpayments();
        this.loadingPromise = new Promise(resolve => (this.loadResolve = resolve));
    }

    public showLoadProgress(prog: number) {
        FBInstant.setLoadingProgress(prog);
    }

    public async loadComplete(): Promise<ISGUser> {
        await FBInstant.startGameAsync();
        const loadEndTime = Date.now();
        const loadDuration = loadEndTime - loadStartTime;
        FBInstant.logEvent('Initial_load_time', loadDuration, {
            time: loadDuration.toString(),
        });

        Core.sdk.setInitialLoadTime(loadDuration);

        const friends = await this.getFriends();

        const entryPointData = FBInstant.getEntryPointData();

        const userData = {
            name: FBInstant.player.getName(),
            userId: FBInstant.player.getID(),
            avatar: FBInstant.player.getPhoto(),
            locale: FBInstant.getLocale(),
            friends,
            entryPointData,
        };

        this.loadResolve(userData);

        return userData;
    }

    public async retrieveData(key: string): Promise<any> {
        let value = null;
        try {
            const data = await FBInstant.player.getDataAsync([key]);
            value = data[key] ? data[key] : null;
        } catch (e) {
            console.warn(`Could not retrieve data from FB. ${e}`);
        }
        return value;
    }

    public async storeData(key: string, value: any) {
        return FBInstant.player.setDataAsync({
            [key]: value,
        });
    }

    public async getAd(placement: string, adType: string): Promise<any> {
        let ad = null;

        if (adType === 'rewarded') {
            await FBInstant.getRewardedVideoAsync(placement).then(rv => {
                ad = rv;
                return ad.loadAsync();
            });
        } else {
            await FBInstant.getInterstitialAdAsync(placement).then(rv => {
                ad = rv;
                return ad.loadAsync();
            });
        }

        return ad;
    }

    public async showLoadedAd(instance: any) {
        return instance.showAsync();
    }

    public async askFriend(friend: IFBFriend, item: IRewardItem, gift: IPayloadGiftData) {
        try {
            const { userId } = friend;

            const payload = isIOS
                ? {}
                : {
                      type: PayloadType.HELPER_GIFT,
                      userId: Core.sdk.user.userId,
                      userName: Core.sdk.user.name,
                      rewardedUserId: userId,
                      helperItem: item.id,
                      gift,
                  };
            if (userId) {
                await this.createContext(userId);
                HelperTracking.track(item.id, HelperTrackStages.CONFIRM);
                this.sendCustomMessage(
                    `helperMessage${isIOS ? 'IOS' : ''}`,
                    await InviteBotImage.getHelperBase64Async(),
                    userId,
                    payload
                );
                return true;
            } else {
                return await Core.sdk.messages.sendInvite();
            }
        } catch (e) {
            return false;
        }
    }

    public async getContextPlayers() {
        let contextPlayers: ContextPlayer[] = [];
        try {
            if (FBInstant.context.getType() !== 'SOLO') {
                contextPlayers = await FBInstant.context.getPlayersAsync();
                contextPlayers = contextPlayers.filter(x => x.getID() !== FBInstant.player.getID());
            }
        } catch (e) {
            Sentry.captureMessage('Could not get context player ' + e);
        }
        return contextPlayers;
    }

    public async getRandomFriend() {
        let friend = defaultFriend;
        try {
            const friends = await this.getFriends();
            const contextPlayer = (await this.getContextPlayers())[0];
            const contextPlayerId = contextPlayer ? contextPlayer.getID() : -1;
            const randomFriend = () =>
                friends.length === 0 ? defaultFriend : friends[getRandom(0, friends.length - 1)];

            friend = randomFriend();
            if (friends.length === 1 && friend.userId === contextPlayerId) {
                friend = defaultFriend;
            } else {
                while (friend.userId === contextPlayerId) {
                    friend = randomFriend();
                }
            }
        } catch (e) {
            Sentry.captureException(e);
            Sentry.captureMessage('Could not get random friend ' + e);
            friend = defaultFriend;
        }

        return friend;
    }

    public async getFriends(): Promise<IFBFriend[]> {
        const fbList = await FBInstant.player.getConnectedPlayersAsync();
        return fbList.map(player => {
            return {
                userId: player.getID(),
                name: player.getName(),
                avatar: player.getPhoto(),
            };
        });
    }

    public async share(sharePayload: IFBSharePayload): Promise<any> {
        return await FBInstant.shareAsync(sharePayload);
    }

    /**
     * @see {@link https://confluence.softgames.de/display/GDEV/v13.4.0-modules-social-play-with-friends}
     */
    public async playWithFriends(shareText: string): Promise<ContextPlayer[]> {
        const filters = ['NEW_CONTEXT_ONLY', 'NEW_PLAYERS_ONLY'] as ContextFilter[];
        const options = {
            filters,
        };

        // I don't think calling updateAsync after this is necessary (as described in confluence removing sdk page), chooseAsync switches the context if possible and one is choosen
        // therefore 'shareText' will not be used for anything
        await FBInstant.context.chooseAsync(options);
        return await FBInstant.context.getPlayersAsync();
    }

    public getRewardedEnabled() {
        return FBInstant.getSupportedAPIs().indexOf('getRewardedVideoAsync') !== -1;
    }

    public async canSubscribeBots() {
        return await FBInstant.player.canSubscribeBotAsync();
    }

    public async subscribeBots() {
        return await FBInstant.player.subscribeBotAsync();
    }

    public async canCreateShortcut() {
        return await FBInstant.canCreateShortcutAsync();
    }

    public async createShortcut() {
        return await FBInstant.createShortcutAsync();
    }

    public canSwitchGame(): boolean {
        return FBInstant.getSupportedAPIs().indexOf('startGameAsync') !== -1;
    }

    public async switchGame(appId: string, gameName: string, placement: string) {
        try {
            await FBInstant.switchGameAsync(appId, {
                originGameId: Core.sdk.gameId,
                originPlayerId: Core.sdk.user.userId,
                placement,
                promoReward: false,
            });
            XPromoTracking.xPromoAccept(gameName);
        } catch (e) {
            if (e.code !== 'USER_INPUT') {
                Sentry.captureMessage(JSON.stringify(e));
            } else {
                XPromoTracking.xPromoDeny(gameName);
            }
        }
    }

    public async sendCustomMessage(
        textKey: string,
        image: string,
        friendId?: string,
        payload?: IPayloadData,
        cta?: string
    ): Promise<any> {
        // friendId not being used now....but it is added to payload, so should be fine
        return await FBInstant.updateAsync({
            action: 'CUSTOM',
            cta: cta || 'Play Now',
            image,
            text: {
                default: Core.local.getTextByLanguageID('en', textKey),
                localizations: {
                    en_US: Core.local.getTextByLanguageID('en', textKey),
                    en_GB: Core.local.getTextByLanguageID('en', textKey),
                    de_DE: Core.local.getTextByLanguageID('de', textKey),
                    es_ES: Core.local.getTextByLanguageID('es', textKey),
                    es_MX: Core.local.getTextByLanguageID('es', textKey),
                    fr_FR: Core.local.getTextByLanguageID('fr', textKey),
                    fr_CA: Core.local.getTextByLanguageID('fr', textKey),
                    it_IT: Core.local.getTextByLanguageID('it', textKey),
                    pt_PT: Core.local.getTextByLanguageID('pt', textKey),
                    ru_RU: Core.local.getTextByLanguageID('ru', textKey),
                    tr_TR: Core.local.getTextByLanguageID('tr', textKey),
                    nl_BE: Core.local.getTextByLanguageID('nl', textKey),
                    nl_NL: Core.local.getTextByLanguageID('nl', textKey),
                    pl_PL: Core.local.getTextByLanguageID('pl', textKey),
                    vi_VN: Core.local.getTextByLanguageID('vi', textKey),
                    hi_IN: Core.local.getTextByLanguageID('hi', textKey),
                    pt_BR: Core.local.getTextByLanguageID('pt-BR', textKey),
                    th_TH: Core.local.getTextByLanguageID('th', textKey),
                },
            },
            template: 'play_turn',
            data: { entryPoint: 'customMessage', payload },
            strategy: 'IMMEDIATE',
            notification: 'NO_PUSH',
        });
    }

    public async gameTracking(trackData: ISGTrackData) {
        return FBInstant.logEvent(trackData.event + '_' + trackData.dimension1);
    }

    public async createContext(friendId: string): Promise<any> {
        return await FBInstant.context.createAsync(friendId);
    }

    public getPlatform(): string {
        return FBInstant.getPlatform();
    }

    public async getEntryPointData(): Promise<any> {
        return FBInstant.getEntryPointAsync();
    }

    public track(data: IFBTrackData, store?: boolean) {
        // logger.debug(['tracking to fb direct', data]);
        try {
            FBInstant.logEvent(data.eventName, data.valueToSum, data.params);
        } catch (e) {
            // logger.debug('Error with fb tracking: ' + e);
            throw e;
        }
    }
}
